import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatCard, Pill, Btn } from "@/components/kit";
import { fmtTime } from "@/lib/format";
import { PhoneCall, PhoneIncoming, TrendingUp, ArrowUpRight, Play, Plus, X, Bot, BookOpen, Trash2 } from "lucide-react";
import { toast } from "sonner";

const SENT_TONE = { positive: "green", neutral: "slate", negative: "danger" };
const OUTCOME_TONE = { appointment_set: "green", lead_created: "blue", faq_answered: "navy", escalated: "amber" };

function SimulateModal({ onClose, onDone }) {
  const [scenario, setScenario] = useState("Hi, this is Dana Whitmore at 4821 Oak Ridge Dr. We got hit by that hailstorm last week and I've got shingles all over my yard. My roof is about 14 years old. Can someone come look at it? My number is 214-555-8890.");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    setLoading(true);
    try { const { data } = await api.post("/receptionist/simulate", { scenario, channel: "voice" }); setResult(data); toast.success("Lead auto-created in CRM"); onDone(); }
    catch { toast.error("AI failed"); } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="mb-card p-6 w-full max-w-lg animate-scale-in" onClick={(e) => e.stopPropagation()} data-testid="simulate-modal">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-display text-2xl font-bold uppercase text-navy flex items-center gap-2"><Bot className="w-5 h-5" /> Simulate Inbound Call</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <p className="text-sm text-slate-500 mb-2">Paste a caller transcript. The AI captures details, answers FAQs, and creates the Contact + Lead automatically.</p>
        <textarea data-testid="scenario-input" value={scenario} onChange={(e) => setScenario(e.target.value)} rows={5}
          className="w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy" />
        <Btn variant="red" onClick={run} disabled={loading} className="w-full mt-3" testid="run-simulate">
          <PhoneIncoming className="w-4 h-4" /> {loading ? "AI answering…" : "Answer with AI"}
        </Btn>
        {result && (
          <div className="mt-4 bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm space-y-1" data-testid="simulate-result">
            <div><b>{result.extracted.caller_name}</b> · {result.extracted.phone}</div>
            <div className="text-slate-600">Intent: <Pill tone="navy">{result.extracted.intent}</Pill> Confidence: {result.extracted.confidence}%</div>
            <div className="text-slate-600">{result.extracted.summary}</div>
            <div className="text-navy font-medium mt-2">AI reply: "{result.extracted.ai_reply}"</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Receptionist() {
  const [metrics, setMetrics] = useState({});
  const [calls, setCalls] = useState([]);
  const [kb, setKb] = useState([]);
  const [modal, setModal] = useState(false);
  const [newKb, setNewKb] = useState({ q: "", a: "" });

  const load = () => {
    api.get("/receptionist/metrics").then((r) => setMetrics(r.data));
    api.get("/calls").then((r) => setCalls(r.data));
    api.get("/knowledge-base").then((r) => setKb(r.data));
  };
  useEffect(() => { load(); }, []);

  const addKb = async () => {
    if (!newKb.q || !newKb.a) return;
    await api.post("/knowledge-base", newKb); setNewKb({ q: "", a: "" }); load(); toast.success("FAQ added");
  };
  const delKb = async (id) => { await api.delete(`/knowledge-base/${id}`); load(); };

  return (
    <div className="space-y-6">
      <PageHeader title="AI Receptionist" subtitle="24/7 answered calls & SMS — a missed call never needs a human to enter the customer"
        actions={<Btn variant="red" onClick={() => setModal(true)} testid="simulate-call-btn"><PhoneIncoming className="w-4 h-4" /> Simulate Call</Btn>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard testid="rec-volume" label="Call Volume" value={metrics.call_volume ?? "—"} icon={PhoneCall} />
        <StatCard testid="rec-answer" label="Answer Rate" value={`${metrics.answer_rate ?? 0}%`} icon={PhoneIncoming} tone="green" />
        <StatCard testid="rec-appt" label="Appt Set Rate" value={`${metrics.appointment_set_rate ?? 0}%`} icon={TrendingUp} tone="navy" />
        <StatCard testid="rec-escalation" label="Escalation Rate" value={`${metrics.escalation_rate ?? 0}%`} icon={ArrowUpRight} tone="amber" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 mb-card p-5">
          <h3 className="font-display font-bold uppercase text-lg text-navy mb-3">Call Log & Transcripts</h3>
          <div className="space-y-3">
            {calls.map((c) => (
              <div key={c.id} data-testid={`call-${c.id}`} className="border border-slate-200 rounded-xl p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-semibold text-navy">{c.caller_name} <span className="text-slate-400 font-normal text-sm">· {c.phone}</span></div>
                    <div className="text-xs text-slate-400">{fmtTime(c.created_at)} · {c.channel} · {c.duration_sec}s</div>
                  </div>
                  <div className="flex gap-1.5 flex-wrap justify-end">
                    <Pill tone={OUTCOME_TONE[c.outcome] || "slate"}>{c.outcome?.replace(/_/g, " ")}</Pill>
                    <Pill tone={SENT_TONE[c.sentiment]}>{c.sentiment}</Pill>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mt-2">{c.summary}</p>
                <div className="flex items-center gap-3 mt-2">
                  <button className="flex items-center gap-1 text-xs text-navy font-semibold"><Play className="w-3 h-3" /> Play recording</button>
                  <span className="text-xs text-slate-400">Intent: {c.intent} · Confidence {c.confidence}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-card p-5">
          <div className="flex items-center gap-2 mb-3"><BookOpen className="w-5 h-5 text-navy" /><h3 className="font-display font-bold uppercase text-lg text-navy">Knowledge Base</h3></div>
          <div className="space-y-2 mb-4 max-h-72 overflow-auto mb-scroll">
            {kb.map((k) => (
              <div key={k.id} className="bg-slate-50 rounded-lg p-3 group" data-testid={`kb-${k.id}`}>
                <div className="flex justify-between"><div className="text-sm font-semibold text-navy">{k.q}</div>
                  <button onClick={() => delKb(k.id)} className="opacity-0 group-hover:opacity-100 text-dangerr"><Trash2 className="w-3.5 h-3.5" /></button></div>
                <div className="text-xs text-slate-500 mt-1">{k.a}</div>
              </div>
            ))}
          </div>
          <input data-testid="kb-q" value={newKb.q} onChange={(e) => setNewKb({ ...newKb, q: e.target.value })} placeholder="Question"
            className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm mb-2 outline-none focus:border-navy" />
          <textarea data-testid="kb-a" value={newKb.a} onChange={(e) => setNewKb({ ...newKb, a: e.target.value })} placeholder="Answer" rows={2}
            className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm mb-2 outline-none focus:border-navy" />
          <Btn variant="ghost" onClick={addKb} className="w-full" testid="add-kb"><Plus className="w-4 h-4" /> Add FAQ</Btn>
        </div>
      </div>
      {modal && <SimulateModal onClose={() => setModal(false)} onDone={load} />}
    </div>
  );
}
